/**
 * "Generate with open items?": asked before a final plan set is generated with gaps (lib/permitEquipment.ts). It lists
 * what is missing, each with Locate, and needs the box ticked: the PDF is never stamped, it leaves each open item (and
 * every value that depends on it) as a fill-in blank, and the Open items checklist lists them. The usual Lux
 * confirmation follows. The preview is never blocked; only this final generation asks.
 */
import { ClipboardList, Crosshair, FileCheck2 } from "lucide-react";
import { useRef, useState } from "react";
import { Button, Modal } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import { openItemsConfirm } from "../lib/permitEquipment";
import type { Check } from "./PermitWorkspace";

export function OpenItemsConfirm({ gaps, costLabel, busy, onConfirm, onLocate, onClose }: {
  gaps: Check[];
  costLabel: string | null;
  busy?: boolean;
  onConfirm: (anchor: Element | null) => void;
  onLocate: (id: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [ack, setAck] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const copy = openItemsConfirm(t, gaps.length, costLabel);
  return (
    <Modal
      title={
        <span className="row" style={{ gap: 8 }}>
          <ClipboardList size={18} style={{ color: "var(--warn)" }} /> {copy.title}
        </span>
      }
      onClose={onClose}
      width={560}
      describedBy="pw-open-items-body"
      footer={
        <>
          <Button onClick={onClose}>{t("permit.openItems.completeFirst")}</Button>
          <Button ref={confirmRef} variant="primary" icon={<FileCheck2 size={16} />} disabled={!ack} loading={busy} onClick={() => onConfirm(confirmRef.current)}>
            {copy.confirm}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 12 }} id="pw-open-items-body">
        <p style={{ margin: 0 }}>
          {copy.body} {copy.price}
        </p>
        <ul className="pw-gap-list pw-open-list">
          {gaps.map((g) => (
            <li key={g.id}>
              <div>
                <b>{g.title}</b>
                <div className="small muted">{g.detail}</div>
              </div>
              <button
                className="pw-mini"
                onClick={() => {
                  onClose();
                  onLocate(g.id);
                }}
              >
                <Crosshair size={13} /> {t("permit.openItems.locate")}
              </button>
            </li>
          ))}
        </ul>
        <label className="pw-ack">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> {copy.ack}
        </label>
      </div>
    </Modal>
  );
}
